/**
 * Selector Resolution Engine
 * Resolves Alignment Based selectors to specific players based on formation analysis
 *
 * Supports all 23 alignment based selectors:
 * - Position-based: leftmost/rightmost/inside/outside receivers
 * - Play-direction-aware: playside/backside receiver/tackle
 * - Depth-based: deepest/shallow/deep receivers
 * - Strength-based: strong/weak side receivers/TE
 * - Count-based: Nth receiver from left/right
 * - Numbered: #1-#5 receiver (side-aware, outside-in ordering)
 */

import type { Player } from '@/components/canvas/BaseCanvas.types'
import type { PlayDirection, RelativeSelector } from '@/types/Concept.types'

interface Formation {
	players: Player[]
}

export interface SelectorOptions {
	callSide?: 'left' | 'right' | 'strong' | 'weak' | null
	formationStrength?: 'left' | 'right'
}

/**
 * Resolve an alignment based selector to a specific player
 * @param selector The alignment based selector to resolve
 * @param formation The formation with player positions
 * @param playDirection The play direction (required for playside/backside selectors)
 * @param count Optional count parameter for selectors that accept N (e.g., leftmost N receivers)
 * @param options Optional side context for numbered receiver selectors
 * @returns The resolved player, or null if no match found
 */
export function resolveSelector(
	selector: RelativeSelector,
	formation: Formation,
	playDirection?: PlayDirection,
	count?: number,
	options?: SelectorOptions,
): Player | null {
	const receivers = getReceivers(formation.players)
	const linemen = getLinemen(formation.players)

	switch (selector) {
		// === POSITION-BASED SELECTORS ===

		case 'leftmost_receiver':
			return receivers.sort((a, b) => a.x - b.x)[0] || null

		case 'rightmost_receiver':
			return receivers.sort((a, b) => b.x - a.x)[0] || null

		case 'second_receiver_from_left':
			return receivers.sort((a, b) => a.x - b.x)[1] || null

		case 'second_receiver_from_right':
			return receivers.sort((a, b) => b.x - a.x)[1] || null

		// === PLAY-DIRECTION-AWARE SELECTORS ===

		case 'playside_receiver':
			if (!playDirection || playDirection === 'na') return null
			return playDirection === 'right'
				? resolveSelector('rightmost_receiver', formation, playDirection)
				: resolveSelector('leftmost_receiver', formation, playDirection)

		case 'backside_receiver':
			if (!playDirection || playDirection === 'na') return null
			return playDirection === 'right'
				? resolveSelector('leftmost_receiver', formation, playDirection)
				: resolveSelector('rightmost_receiver', formation, playDirection)

		case 'playside_tackle': {
			if (!playDirection || playDirection === 'na') return null
			const tackles = linemen.filter(p => p.label === 'LT' || p.label === 'RT')
			if (playDirection === 'right') {
				return tackles.find(p => p.label === 'RT') || null
			} else {
				return tackles.find(p => p.label === 'LT') || null
			}
		}

		case 'backside_tackle': {
			if (!playDirection || playDirection === 'na') return null
			const backTackles = linemen.filter(p => p.label === 'LT' || p.label === 'RT')
			if (playDirection === 'right') {
				return backTackles.find(p => p.label === 'LT') || null
			} else {
				return backTackles.find(p => p.label === 'RT') || null
			}
		}

		// === DEPTH-BASED SELECTORS ===

		case 'deepest_receiver':
			// Deepest = furthest upfield (highest Y value)
			return receivers.sort((a, b) => b.y - a.y)[0] || null

		// === INSIDE/OUTSIDE SELECTORS ===

		case 'inside_receivers': {
			// Inside receivers are the middle receivers (not leftmost or rightmost)
			if (receivers.length < 3) return null
			const sorted = receivers.sort((a, b) => a.x - b.x)
			// Return the innermost receiver (middle of the formation)
			const middleIndex = Math.floor(sorted.length / 2)
			return sorted[middleIndex] || null
		}

		case 'outside_receivers': {
			// Outside receivers are the leftmost and rightmost
			// Return leftmost for now (could return both in future)
			return resolveSelector('leftmost_receiver', formation, playDirection)
		}

		// === STRENGTH-BASED SELECTORS ===

		case 'strong_side_receivers': {
			// Strong side = side with more receivers
			const leftReceivers = receivers.filter(p => p.x < 0)
			const rightReceivers = receivers.filter(p => p.x > 0)

			if (rightReceivers.length > leftReceivers.length) {
				// Strong side is right, return rightmost
				return rightReceivers.sort((a, b) => b.x - a.x)[0] || null
			} else {
				// Strong side is left (or equal), return leftmost
				return leftReceivers.sort((a, b) => a.x - b.x)[0] || null
			}
		}

		case 'weak_side_receivers': {
			// Weak side = side with fewer receivers
			const leftReceivers = receivers.filter(p => p.x < 0)
			const rightReceivers = receivers.filter(p => p.x > 0)

			if (rightReceivers.length > leftReceivers.length) {
				// Weak side is left
				return leftReceivers.sort((a, b) => a.x - b.x)[0] || null
			} else {
				// Weak side is right (or equal)
				return rightReceivers.sort((a, b) => b.x - a.x)[0] || null
			}
		}

		case 'strong_side_te': {
			// Find TE on the side with more receivers
			const tightEnds = formation.players.filter(p =>
				p.label?.toUpperCase() === 'TE' ||
				p.label?.toUpperCase() === 'Y',
			)
			if (tightEnds.length === 0) return null

			const leftReceivers = receivers.filter(p => p.x < 0)
			const rightReceivers = receivers.filter(p => p.x > 0)

			const strongSideIsRight = rightReceivers.length > leftReceivers.length

			// Return TE on strong side
			return tightEnds.find(te =>
				strongSideIsRight ? te.x > 0 : te.x < 0,
			) || tightEnds[0] || null
		}

		// === DEPTH THRESHOLD SELECTORS ===

		case 'shallow_receivers': {
			// Shallow receivers are closer to LOS (lower Y values)
			// Return shallowest receiver
			return receivers.sort((a, b) => a.y - b.y)[0] || null
		}

		case 'deep_receivers': {
			// Deep receivers are further from LOS (higher Y values)
			// Return deepest receiver
			return receivers.sort((a, b) => b.y - a.y)[0] || null
		}

		// === COUNT-BASED SELECTORS (with count parameter) ===

		case 'leftmost_receivers': {
			// Return the Nth leftmost receiver
			const n = count || 1
			const sorted = receivers.sort((a, b) => a.x - b.x)
			return sorted[n - 1] || null
		}

		case 'rightmost_receivers': {
			// Return the Nth rightmost receiver
			const n = count || 1
			const sorted = receivers.sort((a, b) => b.x - a.x)
			return sorted[n - 1] || null
		}

		// === NUMBERED RECEIVER SELECTORS (side-aware) ===

		case '#1_receiver':
		case '#2_receiver':
		case '#3_receiver':
		case '#4_receiver':
		case '#5_receiver': {
			const n = parseInt(selector.charAt(1))
			return resolveNumberedReceiver(n, receivers, options?.callSide, options?.formationStrength)
		}

		default:
			console.warn(`Unknown selector: ${selector}`)
			return null
	}
}

/**
 * Resolve a selector to multiple players (for selectors that can match multiple)
 * @param selector The alignment based selector to resolve
 * @param formation The formation with player positions
 * @param playDirection The play direction
 * @param count How many players to return
 * @returns Array of resolved players
 */
export function resolveSelectorMultiple(
	selector: RelativeSelector,
	formation: Formation,
	playDirection?: PlayDirection,
	count: number = 1,
): Player[] {
	const receivers = getReceivers(formation.players)

	switch (selector) {
		case 'leftmost_receivers': {
			return receivers.sort((a, b) => a.x - b.x).slice(0, count)
		}

		case 'rightmost_receivers': {
			return receivers.sort((a, b) => b.x - a.x).slice(0, count)
		}

		case 'inside_receivers': {
			// Return all inside receivers (not leftmost or rightmost)
			if (receivers.length < 3) return []
			const sorted = receivers.sort((a, b) => a.x - b.x)
			return sorted.slice(1, -1) // Exclude first and last
		}

		case 'outside_receivers': {
			// Return leftmost and rightmost
			const sorted = receivers.sort((a, b) => a.x - b.x)
			if (sorted.length < 2) return sorted
			const first = sorted[0]
			const last = sorted[sorted.length - 1]
			if (!first || !last) return sorted
			return [first, last]
		}

		case 'shallow_receivers': {
			// Return N shallowest receivers
			return receivers.sort((a, b) => a.y - b.y).slice(0, count)
		}

		case 'deep_receivers': {
			// Return N deepest receivers
			return receivers.sort((a, b) => b.y - a.y).slice(0, count)
		}

		case 'strong_side_receivers': {
			// Return all receivers on strong side
			const leftReceivers = receivers.filter(p => p.x < 0)
			const rightReceivers = receivers.filter(p => p.x > 0)

			return rightReceivers.length > leftReceivers.length
				? rightReceivers
				: leftReceivers
		}

		case 'weak_side_receivers': {
			// Return all receivers on weak side
			const leftReceivers = receivers.filter(p => p.x < 0)
			const rightReceivers = receivers.filter(p => p.x > 0)

			return rightReceivers.length > leftReceivers.length
				? leftReceivers
				: rightReceivers
		}

		default: {
			// For single-result selectors, wrap in array
			const single = resolveSelector(selector, formation, playDirection, count)
			return single ? [single] : []
		}
	}
}

// === NUMBERED RECEIVER RESOLUTION ===

/** Stacked/bunched receiver X-position epsilon (pixels) */
const STACKED_EPSILON = 5

/**
 * Resolve a numbered receiver (#1 = outermost, #5 = innermost) on a given side.
 * Sorts eligible receivers outside-in with Y-position tiebreaker for stacked/bunched players.
 */
function resolveNumberedReceiver(
	n: number,
	receivers: Player[],
	callSide?: 'left' | 'right' | 'strong' | 'weak' | null,
	formationStrength?: 'left' | 'right',
): Player | null {
	// 1. Determine the absolute side
	let side: 'left' | 'right'
	if (callSide === 'strong' || callSide === 'weak') {
		const strength = formationStrength ?? 'right'
		side = callSide === 'strong' ? strength : (strength === 'right' ? 'left' : 'right')
	} else if (callSide === 'left' || callSide === 'right') {
		side = callSide
	} else {
		// No side specified — default to right (most common in football)
		side = 'right'
	}

	// 2. Center X = 0 (formations are centered on canvas)
	const centerX = 0

	// 3. Split receivers by side
	const sideReceivers = side === 'right'
		? receivers.filter(p => p.x > centerX)
		: receivers.filter(p => p.x < centerX)

	// 4. Sort outside-in with stacked tiebreaker
	if (side === 'right') {
		sideReceivers.sort((a, b) => {
			const xDiff = b.x - a.x
			if (Math.abs(xDiff) > STACKED_EPSILON) return xDiff
			// Tiebreaker: closer to LOS (lower Y) = lower number
			return a.y - b.y
		})
	} else {
		sideReceivers.sort((a, b) => {
			const xDiff = a.x - b.x
			if (Math.abs(xDiff) > STACKED_EPSILON) return xDiff
			// Tiebreaker: closer to LOS (lower Y) = lower number
			return a.y - b.y
		})
	}

	// 5. Return Nth receiver (1-indexed)
	return sideReceivers[n - 1] ?? null
}

// === HELPER FUNCTIONS ===

/**
 * Get all receivers from formation (non-linemen, non-QB)
 */
function getReceivers(players: Player[]): Player[] {
	return players.filter(p =>
		!p.isLineman &&
		p.label?.toUpperCase() !== 'QB' &&
		p.label !== '', // Filter out unlabeled players
	)
}

/**
 * Get all linemen from formation
 */
function getLinemen(players: Player[]): Player[] {
	return players.filter(p => p.isLineman === true)
}

/**
 * Validate that a selector is supported
 */
export function isSelectorSupported(selector: string): boolean {
	const supportedSelectors: RelativeSelector[] = [
		'leftmost_receiver',
		'rightmost_receiver',
		'leftmost_receivers',
		'rightmost_receivers',
		'second_receiver_from_left',
		'second_receiver_from_right',
		'inside_receivers',
		'outside_receivers',
		'playside_receiver',
		'backside_receiver',
		'playside_tackle',
		'backside_tackle',
		'strong_side_receivers',
		'weak_side_receivers',
		'strong_side_te',
		'deepest_receiver',
		'shallow_receivers',
		'deep_receivers',
		'#1_receiver',
		'#2_receiver',
		'#3_receiver',
		'#4_receiver',
		'#5_receiver',
	]
	return supportedSelectors.includes(selector as RelativeSelector)
}
